// components/FiltrarMedios/FiltrarMedios.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../../Navbar';
import {
  Button,
  Container,
  IconButton,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import MaterialReactTable from 'material-react-table';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AddBoxTwoToneIcon from '@mui/icons-material/AddBoxTwoTone';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { Delete } from '@mui/icons-material';
import { getData } from '../../../services/library';
import FiltrarMediosModal from './FiltrarMediosModal';
import { filtrarMediosService } from './FiltrarMediosService';

function FiltrarMedios() {
  const [filtros, setFiltros] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [medios, setMedios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFiltro, setEditingFiltro] = useState(null);
  const [tabla, setTabla] = useState(true);

  // Obtener filtros
  const obtenerFiltros = async () => {
    try {
      setLoading(true);
      const data = await filtrarMediosService.getAll();
      setFiltros(data);
    } catch (error) {
      console.error('Error cargando filtros:', error);
    } finally {
      setLoading(false);
    }
  };

  // Obtener choferes y medios para los selects
  const obtenerDatosRelacionados = async () => {
    try {
      const [choferesData, mediosData] = await Promise.all([
        filtrarMediosService.getChoferes(),
        filtrarMediosService.getMediosDisponibles(),
      ]);
      setChoferes(choferesData);
      setMedios(mediosData);
    } catch (error) {
      console.error('Error cargando datos relacionados:', error);
    }
  };

  useEffect(() => {
    obtenerFiltros();
    obtenerDatosRelacionados();
  }, [tabla]);

  const handleClickNuevo = () => {
    setEditingFiltro(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingFiltro(null);
    setTabla(true);
    obtenerFiltros();
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        maxSize: 80,
      },
      {
        accessorKey: 'nombre_apellido',
        header: 'Chofer',
        size: 200,
      },
      {
        accessorKey: 'filtro_nombre',
        header: 'Nombre del Filtro',
        size: 200,
      },
      {
        accessorKey: 'medios_incluidos',
        header: 'Medios Permitidos',
        size: 300,
        Cell: ({ row }) => {
          const mediosIds = row.original.medios_incluidos;
          if (!mediosIds) {
            return 'Todos los medios';
          }
          const ids = mediosIds.split(',').map((id) => parseInt(id));
          const nombres = ids.map((id) => {
            const medio = medios.find((m) => m.id === id);
            return medio ? medio.nombre : `ID ${id}`;
          });
          return nombres.join(', ');
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Creado',
        size: 150,
        Cell: ({ row }) =>
          new Date(row.original.created_at).toLocaleDateString('es-AR'),
      },
    ],
    [medios]
  );

  const muiSearchTextFieldProps = {
    placeholder: 'Buscar..',
    sx: { minWidth: '18rem', backgroundColor: '#ffffff' },
    variant: 'outlined',
    color: 'success',
    size: 'small',
  };

  const initialState = {
    density: 'compact',
    showGlobalFilter: true,
    pagination: { pageIndex: 0, pageSize: 10 },
  };

  const tableHeadCellProps = {
    align: 'left',
    sx: {
      fontSize: 16,
      color: 'white',
      fontWeight: 'normal',
      backgroundColor: '#2471A3',
      borderColor: 'black',
    },
  };

  const displayColumnDefOptions = {
    'mrt-row-actions': {
      muiTableHeadCellProps: {
        align: 'center',
      },
      muiTableBodyCellProps: {
        align: 'center',
      },
    },
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" disableGutters sx={{ mb: 2, mt: 1 }}>
        {tabla && (
          <MaterialReactTable
            localization={MRT_Localization_ES}
            columns={columns}
            data={filtros ?? [{}]}
            layoutMode={'grid'}
            initialState={initialState}
            enableColumnActions={false}
            enableGlobalFilterModes={false}
            enableHiding={false}
            enableRowActions={true}
            enableColumnFilters={false}
            enableFullScreenToggle={false}
            positionActionsColumn={'last'}
            enableExpandAll={false}
            enableToolbarInternalActions={true}
            muiTableHeadCellProps={tableHeadCellProps}
            muiSearchTextFieldProps={muiSearchTextFieldProps}
            enableDensityToggle={false}
            displayColumnDefOptions={displayColumnDefOptions}
            enableColumnResizing={true}
            renderTopToolbarCustomActions={() => (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddBoxTwoToneIcon />}
                  onClick={handleClickNuevo}
                >
                  Nuevo Filtro
                </Button>
                <Typography variant="poster" fontSize={26}>
                  <FilterAltIcon sx={{ fontSize: 20 }} />
                  Filtros de Medios de Pago
                </Typography>
              </>
            )}
            renderRowActionMenuItems={({ closeMenu, row, index }) => [
              <MenuItem
                key={index}
                onClick={() => {
                  closeMenu();
                  setEditingFiltro(row.original);
                  setModalOpen(true);
                }}
                sx={{ m: 0 }}
              >
                <ListItemIcon>
                  <BorderColorIcon color="primary" />
                </ListItemIcon>
                Editar
              </MenuItem>,
              <MenuItem
                key={index + 1}
                onClick={() => {
                  closeMenu();
                  if (window.confirm('¿Está seguro de eliminar este filtro?')) {
                    filtrarMediosService.delete(row.original.id).then(() => {
                      obtenerFiltros();
                    });
                  }
                }}
                sx={{ m: 0 }}
              >
                <ListItemIcon>
                  <Delete color="error" />
                </ListItemIcon>
                Eliminar
              </MenuItem>,
            ]}
          />
        )}

        <FiltrarMediosModal
          open={modalOpen}
          onClose={handleCloseModal}
          editingFiltro={editingFiltro}
          choferes={choferes}
          medios={medios}
          onSuccess={obtenerFiltros}
        />
      </Container>
    </>
  );
}

export default FiltrarMedios;